import {Box, Flex, Label} from 'exsportia-components'
import {FC, useRef, useState} from 'react'

type Props = {
  id: string
  label?: string
  onChange: (value: string) => void
  error?: string
}

export const OTPInput: FC<Props> = ({label, onChange, error}) => {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleChange = (value: string, index: number) => {
    // Only allow numbers
    const cleanValue = value.replace(/[^0-9]/g, '')
    if (!cleanValue && value !== '') {
      return
    }

    const newOtp = [...otp]
    newOtp[index] = cleanValue.substring(cleanValue.length - 1)
    setOtp(newOtp)

    const otpString = newOtp.join('')
    onChange(otpString)

    // Move to next input if value is entered
    if (cleanValue && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const newOtp = [...otp]
      newOtp[index] = ''
      setOtp(newOtp)
      onChange(newOtp.join(''))
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const data = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6)
    if (!data) {
      return
    }

    const newOtp = data.split('')
    const paddedOtp = [...newOtp, ...new Array(6 - newOtp.length).fill('')]
    setOtp(paddedOtp)

    onChange(data)

    // Focus last filled or next empty
    const nextIndex = data.length < 6 ? data.length : 5
    inputRefs.current[nextIndex]?.focus()
  }

  return (
    <Box>
      {label && (
        <Box mb='8px'>
          <Label settings={{color: error ? 'error' : 'text-secondary'}}>
            {label}
          </Label>
        </Box>
      )}
      <Flex style={{display: 'flex', gap: '8px', justifyContent: 'center'}}>
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type='text'
            inputMode='numeric'
            autoComplete='one-time-code'
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            style={{
              width: '48px',
              height: '48px',
              textAlign: 'center',
              fontSize: '20px',
              fontWeight: 'bold',
              border: `1.5px solid ${error ? '#ED0A34' : 'rgba(0, 16, 61, 0.12)'}`,
              borderRadius: '8px',
              outline: 'none',
              backgroundColor: 'white',
            }}
          />
        ))}
      </Flex>
      {error && (
        <Box
          mt='10px'
          fontSize='12px'
          color='error'
          textAlign='center'
        >
          {error}
        </Box>
      )}
    </Box>
  )
}
